import React from 'react';
import {
    Create,
    Edit,
    FileInput,
    ImageField,
    SimpleForm,
    useRecordContext,
    useSaveContext,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Avatar, Box, Typography} from '@mui/material';
import useTranslatedCountries from '../lib/useTranslatedCountries';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import {UmbrellaNotificationToggles} from './UmbrellaNotificationsCard';

const TIMEZONE_CHOICES = [
    {id: 'Europe/Amsterdam', name: 'Amsterdam (CET)'},
    {id: 'Europe/London', name: 'London (GMT)'},
    {id: 'Europe/Paris', name: 'Paris (CET)'},
    {id: 'Europe/Berlin', name: 'Berlin (CET)'},
    {id: 'America/New_York', name: 'New York (EST)'},
    {id: 'America/Los_Angeles', name: 'Los Angeles (PST)'},
    {id: 'Asia/Dubai', name: 'Dubai (GST)'},
    {id: 'Asia/Singapore', name: 'Singapore (SGT)'},
];

const CURRENCY_CHOICES = [
    {id: 'EUR', name: '€ Euro (EUR)'},
    {id: 'GBP', name: '£ British Pound (GBP)'},
    {id: 'USD', name: '$ US Dollar (USD)'},
    {id: 'CHF', name: 'Swiss Franc (CHF)'},
    {id: 'SEK', name: 'Swedish Krona (SEK)'},
    {id: 'NOK', name: 'Norwegian Krone (NOK)'},
    {id: 'DKK', name: 'Danish Krone (DKK)'},
    {id: 'AED', name: 'UAE Dirham (AED)'},
];

const LOCALE_CHOICES = [
    {id: 'nl_NL', name: 'Nederlands (Nederland)'},
    {id: 'en_GB', name: 'English (United Kingdom)'},
    {id: 'en_US', name: 'English (United States)'},
    {id: 'de_DE', name: 'Deutsch (Deutschland)'},
    {id: 'fr_FR', name: 'Français (France)'},
    {id: 'es_ES', name: 'Español (España)'},
    {id: 'pt_PT', name: 'Português (Portugal)'},
    {id: 'it_IT', name: 'Italiano (Italia)'},
    {id: 'ar_AE', name: 'العربية (الإمارات)'},
];

const urlFormat = (v) => (!v || v === 'null' || v === 'undefined' || v === 'NULL') ? '' : v;
// v2 explicit-value contract: an empty string CLEARS these fields server-side — return
// '' here, not null, so a cleared field still reaches the provider as an explicit value
// instead of being dropped as "untouched."
const urlParse = (v) => (!v || v === 'null' || v === 'undefined' || !v.trim()) ? '' : v;

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton
            variant="primary"
            disabled={saving}
            onClick={handleSubmit(save)}
        >
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const HeaderBar = ({mode, recordName, statusVariant}) => (
    <PageHeader
        breadcrumb={{label: 'Umbrellas', to: '/umbrellas'}}
        title={
            mode === 'create' ? (
                <Box component="span">Create umbrella</Box>
            ) : (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <StatusDot variant={statusVariant}/>
                    <Box component="span">{recordName || 'Untitled'}</Box>
                </Box>
            )
        }
        actions={
            <SaveAction label={mode === 'create' ? 'Create umbrella' : 'Save changes'}/>
        }
    />
);

const LogoPanel = () => {
    const record = useRecordContext();
    const existingLogo = record?.logo_name && record?.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    return (
        <SectionCard title="Logo">
            {existingLogo && (
                <Box sx={{display: 'flex', justifyContent: 'center', mb: 1.5}}>
                    <Avatar
                        src={existingLogo}
                        variant="rounded"
                        sx={{
                            width: 96, height: 96,
                            fontSize: 32,
                            bgcolor: t.color.sidebarHover,
                            color: t.color.text.secondary,
                            border: `1px solid ${t.color.border.hairline}`,
                        }}
                    >
                        {record?.charity_name?.charAt(0)}
                    </Avatar>
                </Box>
            )}
            <FileInput
                source="logo"
                label={false}
                accept={{'image/*': []}}
                maxSize={500000}
                sx={{
                    '& .RaFileInput-dropZone': {
                        border: `1px dashed ${t.color.border.default}`,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: t.color.surface,
                        padding: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: t.color.text.secondary,
                        fontSize: `${t.type.caption.size}px`,
                        '&:hover': {
                            borderColor: t.color.accent.primary,
                            color: t.color.accent.primary,
                        },
                    },
                }}
            >
                <ImageField source="src" title="Logo"/>
            </FileInput>
            <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: 1}}>
                PNG or JPG. Maximum 500 KB.
            </Typography>
        </SectionCard>
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const countries = useTranslatedCountries();

    const statusVariant = record?.demo_charity
        ? 'info'
        : record?.active
            ? 'success'
            : 'neutral';

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                recordName={record?.charity_name}
                statusVariant={statusVariant}
            />

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title="Organization">
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="charity_name" label="Organization name" required/>
                            <StatamicTextInput source="email" label="Email address" type="email" required/>
                            <StatamicTextInput source="city" label="City" required/>
                            <StatamicSelectInput
                                source="country"
                                label="Country"
                                required
                                choices={countries}
                                defaultValue="NL"
                            />
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput
                                source="description"
                                label="Description"
                                required
                                multiline
                                rows={4}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title="Payment">
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput
                                source="payment_link_url"
                                label="Stripe payment link"
                                type="url"
                                format={urlFormat}
                                parse={urlParse}
                            />
                            <StatamicTextInput
                                source="paypal_payment_link_url"
                                label="PayPal payment link"
                                type="url"
                                format={urlFormat}
                                parse={urlParse}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title="Child detail pages">
                        <StatamicTextInput
                            source="child_detail_base_url"
                            label="Child detail base URL"
                            type="url"
                            format={urlFormat}
                            parse={urlParse}
                            helperText="e.g. https://childsponsorship.online/sponsor/sevac"
                        />
                    </SectionCard>

                    <SectionCard title="Messaging">
                        <ToggleRow
                            source="allow_text_messages"
                            label="Text messages"
                            subtitle="Enable text-only posts without media"
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="allow_photo_messages"
                            label="Photo messages"
                            subtitle="Enable posts with photo attachments"
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="allow_video_messages"
                            label="Video messages"
                            subtitle="Enable posts with video attachments"
                            defaultValue={true}
                        />
                    </SectionCard>

                    <SectionCard title="Localization">
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2}}>
                            <StatamicSelectInput
                                source="default_timezone"
                                label="Timezone"
                                choices={TIMEZONE_CHOICES}
                                defaultValue="Europe/Amsterdam"
                            />
                            <StatamicSelectInput
                                source="default_currency"
                                label="Currency"
                                choices={CURRENCY_CHOICES}
                                defaultValue="EUR"
                            />
                            <StatamicSelectInput
                                source="default_locale"
                                label="Default language"
                                choices={LOCALE_CHOICES}
                                defaultValue="nl_NL"
                            />
                        </Box>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title="Status">
                        <ToggleRow
                            source="active"
                            label="Active"
                            subtitle="Enable or disable this organization"
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="demo_charity"
                            label="Demo mode"
                            subtitle="Mark as demo/test organization"
                            defaultValue={false}
                        />
                    </SectionCard>

                    <SectionCard title="Notifications">
                        <UmbrellaNotificationToggles/>
                    </SectionCard>

                    <LogoPanel/>
                </Box>
            </Box>
        </Box>
    );
};

export const UmbrellaEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const UmbrellaCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default UmbrellaEditStatamic;
